// 마음건강 세션 상태 관리 Store
import { create } from 'zustand';
import { SessionStatus, MindHealthSession } from '@/api/mentalHealth/mindHealthSession';

interface SessionState {
  // 현재 진행 중인 세션 정보
  currentSession: MindHealthSession | null;
  
  // 세션 진행 상태
  isSessionActive: boolean;
  
  // 에러 메시지
  error: string | null;
  
  // Actions
  setCurrentSession: (session: MindHealthSession | null) => void;
  updateSessionStatus: (status: SessionStatus, receivedChunkCount?: number) => void;
  clearSession: () => void;
  setError: (error: string | null) => void;
  
  // 세션이 완료되었는지 확인
  isSessionCompleted: () => boolean;
  
  // 세션이 진행 중인지 확인
  isSessionInProgress: () => boolean;
  
  // 진행률 계산
  getProgress: () => number;
}

export const useMindHealthSessionStore = create<SessionState>((set, get) => ({
  currentSession: null,
  isSessionActive: false,
  error: null,

  setCurrentSession: (session) => {
    set({
      currentSession: session,
      isSessionActive: session !== null && 
        (session.status === 'INITIALIZING' || session.status === 'IN_PROGRESS'),
      error: null,
    });
  },

  updateSessionStatus: (status, receivedChunkCount) => {
    const { currentSession } = get();
    if (!currentSession) return;

    const updatedSession: MindHealthSession = {
      ...currentSession,
      status,
      receivedChunkCount: receivedChunkCount ?? currentSession.receivedChunkCount,
    };

    set({
      currentSession: updatedSession,
      isSessionActive: status === 'INITIALIZING' || status === 'IN_PROGRESS',
    });
  },

  clearSession: () => {
    set({
      currentSession: null,
      isSessionActive: false,
      error: null,
    });
  },

  setError: (error) => {
    set({ error });
  },

  isSessionCompleted: () => {
    const { currentSession } = get();
    if (!currentSession) return false;
    return currentSession.status === 'COMPLETED';
  },

  isSessionInProgress: () => {
    const { currentSession } = get();
    if (!currentSession) return false;
    return currentSession.status === 'INITIALIZING' || currentSession.status === 'IN_PROGRESS';
  },

  getProgress: () => {
    const { currentSession } = get();
    if (!currentSession) return 0;
    
    const { receivedChunkCount, expectedTotalChunks } = currentSession;
    if (expectedTotalChunks === 0) return 0;
    
    return Math.min(100, Math.round((receivedChunkCount / expectedTotalChunks) * 100));
  },
}));


